import { computed } from '@vue/composition-api';

import { getNumberFormatted } from '@/helpers/common';

export default (props, context) => {
  const vehicleIcon = computed(() => {
    switch (context.attrs['product-name']) {
      case 'bike':
        return 'mdi-motorbike';
      case 'van':
        return 'mdi-van-utility';
      case 'motorhome':
        return 'mdi-rv-truck';
      case 'caravan':
        return 'mdi-caravan';
      case 'car':
      default:
        return 'mdi-car';
    }
  });

  // Base helper functions
  const getValue = (item, fieldName, prefix) => {
    const value = item[`${fieldName}${prefix}`] ?? item[fieldName];
    return value !== undefined ? value : null;
  };

  const createMapping = (prepend = '', value = null, append = '') => ({
    ...(prepend && { prepend }),
    value,
    ...(append && { append }),
  });

  const getFieldsToMap = (item, prefix, constants) => {
    const causeDesc = getValue(item, 'causeOfDescription', prefix);
    const isAccident = causeDesc === constants.CAUSE_OF_DESCRIPTION_ACCIDENT;
    const typeTheftValue = getValue(item, 'typeTheft', prefix);
    const isTheft = typeTheftValue !== null && constants.DRIVER_CLAIM_TYPE_THEFT[typeTheftValue] !== undefined;
    const isNoClaimMade = [
      constants.DRIVER_CLAIM_CAUSE_INCIDENT_NO_CLAIM_MADE,
      constants.DRIVER_CLAIM_CAUSE_INCIDENT_NO_CLAIM_MADE_STATIC,
      constants.DRIVER_CLAIM_CAUSE_INCIDENT_NO_CLAIM_MADE_TOURING,
    ].includes(causeDesc);

    return {
      // Basic fields - always included if they exist
      basic: ['causeOfDescription', 'dateOfClaim'],

      // Cost fields - included if not a "no claim made" case
      cost: !isNoClaimMade ? ['costOfRepairs'] : [],

      // Accident fields - included if it's an accident
      accident: isAccident ? [
        'settledClaim',
        'anyInjuries',
        ...(getValue(item, 'settledClaim', prefix) === constants.CLAIM_SETTLED && prefix !== 'Static' ? ['singleAccident'] : []),
        'atFault',
      ] : [],

      // Theft fields - included based on theft type
      theft: isTheft ? [
        'typeTheft',
        ...(typeTheftValue === constants.TYPE_THEFT_VEHICLE_CONTENTS ? [prefix === 'Touring' ? 'recoveredCaravan' : 'recoveredVehicle'] : []),
        ...([constants.TYPE_THEFT_VEHICLE_CONTENTS, constants.TYPE_THEFT_CONTENTS_OF_THE_VEHICLE].includes(typeTheftValue) ? ['theftFromHome'] : []),
      ] : [],

      // Policy fields - included only for non-prefix cases
      policy: !prefix ? ['currentPolicy'] : [],
    };
  };

  const createFieldMapping = (item, fieldName, prefix, constants) => {
    const value = getValue(item, fieldName, prefix);
    const causeOfDescriptionList = prefix === 'Touring'
      ? constants.DRIVER_CLAIM_CAUSE_OF_DESCRIPTION_TOURING
      : constants.DRIVER_CLAIM_CAUSE_OF_DESCRIPTION;

    const mappingConfigs = {
      causeOfDescription: () => createMapping('Cause:', causeOfDescriptionList[value]),
      dateOfClaim: () => createMapping('Happened on', value),
      costOfRepairs: () => createMapping('Total claim cost', value != null ? `£ ${getNumberFormatted(value)}` : null),
      settledClaim: () => createMapping('', value ? 'Settled' : 'Not settled', 'claim'),
      anyInjuries: () => createMapping('', `With${!value ? 'out' : ''}`, 'injuries'),
      singleAccident: () => createMapping('', `${!value ? 'Not a ' : ''}Single`, 'Accident'),
      atFault: () => value !== null && constants.DRIVER_CLAIM_AT_FAULT[value] !== undefined
        ? createMapping('', constants.DRIVER_CLAIM_AT_FAULT[value], 'at fault')
        : null,
      typeTheft: () => value !== null && constants.DRIVER_CLAIM_TYPE_THEFT[value] !== undefined
        ? createMapping('Stolen:', constants.DRIVER_CLAIM_TYPE_THEFT[value])
        : null,
      recoveredCaravan: () => createMapping('', value ? 'Recovered' : 'Not recovered', 'vehicle'),
      recoveredVehicle: () => createMapping('', value ? 'Recovered' : 'Not recovered', 'vehicle'),
      theftFromHome: () => createMapping('Theft', `${!value ? 'not ' : ''}from home`),
      currentPolicy: () => createMapping('Occurred on recent policy:', value ? 'Yes' : 'No'),
    };

    return mappingConfigs[fieldName] ? mappingConfigs[fieldName]() : null;
  };

  /**
   * Creates a mapping object for a claim item, used to generate a readable UI preview of claim details.
   * Dynamically determines the relevant field prefix and applies field-specific mapping logic.
   *
   * @param {Object} item - The claim item object containing raw field data.
   * @param {Object} constants - A collection of constants used to translate raw values into human-readable labels.
   * @param {string} productName - The name of the insurance product (e.g., 'caravan', 'car') used to determine prefix logic.
   * @param {string} field - The main question field identifier (e.g., 'hadAnyAccidentsStatic'), used to decide the prefix.
   *
   * @returns {Object} A mapping object where each key is a field name with prefix (e.g., `costOfRepairsTouring`)
   * and each value is an object describing how to present the field, including `prepend`, `value`, and optionally `append`.
   *
   * @example
   * {
   *   costOfRepairsTouring: {
   *     prepend: 'Total claim cost',
   *     value: '£ 1000'
   *   },
   *   atFaultTouring: {
   *     prepend: '',
   *     value: 'Yes',
   *     append: 'at fault'
   *   }
   * }
   */
  const createMappingsClaims = (item, constants, productName, field) => {
    if (!item) return {};

    const prefix = productName === 'caravan' ? (field === 'hadAnyAccidentsStatic' ? 'Static' : 'Touring') : '';
    const fieldsToMap = getFieldsToMap(item, prefix, constants);
    const mappings = {};

    // Combine all fields and create mappings
    Object.values(fieldsToMap)
      .flat()
      .forEach(fieldName => {
        if (Object.prototype.hasOwnProperty.call(item, `${fieldName}${prefix}`) ||
            Object.prototype.hasOwnProperty.call(item, fieldName)) {
          const mapping = createFieldMapping(item, fieldName, prefix, constants);
          if (mapping && mapping.value !== null) {
            mappings[`${fieldName}${prefix}`] = mapping;
          }
        }
      });

    return mappings;
  };

  /**
   * Generates a mapping object for conviction data, creating formatted display labels and values for each field.
   *
   * @param {Object} item - The conviction data item containing information about a specific conviction.
   * @param {Object} constants - Constants object that includes mappings for values such as `DRIVER_CONVICTION_ALCOHOL_METHOD_TEST`.
   * @returns {Object} An object mapping each field to its display configuration, including labels, values, and optional prefixes/suffixes.
   */
  const createMappingsConvictions = (item, constants) => {
    return {
      offenceCode: {
        prepend: 'Reason for conviction:',
        value: item.offenceCode === null ? null : item.offenceCode,
      },
      dateOfConviction: {
        prepend: 'Happened on',
        value: item.dateOfConviction,
      },
      convictionDeclaredAccident: {
        prepend: '',
        value: item.convictionDeclaredAccident ? 'Related' : 'Not related',
        append: 'to a declared accident',
      },
      howMuchYouFined: {
        prepend: 'Fined:',
        value: `£ ${getNumberFormatted(item.howMuchYouFined)}`,
      },
      alcoholMethodTest: {
        prepend: '',
        value: constants.DRIVER_CONVICTION_ALCOHOL_METHOD_TEST[item.alcoholMethodTest],
        append: 'alcohol method test used',
      },
      alcoholLevel: {
        prepend: 'Recorded alcohol level:',
        value: `${item.alcoholLevel} mg/100 ml`,
      },
      pointsReceived: {
        prepend: '',
        value: `${item.pointsReceived || 'No'} point${item.pointsReceived === 1 ? '' : 's'}`,
        append: 'received',
      },
      receivedBanPeriod: {
        prepend: 'Received',
        value: item.receivedBanPeriod > 0 ? `${item.receivedBanPeriod} month${item.receivedBanPeriod > 1 ? 's' : ''}` : 'no',
        append: 'ban period',
      },
    };
  };

  /**
   * Generates a preview list from the data array based on the provided mappings (typically for Claims and Convictions data).
   *
   * The function processes each data object (from a subform or a larger dataset) and creates a formatted preview list.
   * Each field in the object is matched against the provided `mappings`, which control how the fields are presented in the
   * preview. Mappings include configuration for prepending or appending text and setting custom conditions for inclusion.
   *
   * Example `mappings` might be used to structure claims or conviction data for a vehicle insurance form, where each field
   * (such as claim type, date, or amount) is formatted according to the specific needs of the application.
   *
   * @param {Array<Object>} dataArray - Array of data entries, each representing a claim or conviction with a unique `id`.
   * @param {Object} item - The specific item for which the preview list is being generated, including details such as `id` and field values.
   * @param {Object} constants - Constants object containing mappings and values for fields like `DRIVER_CLAIM_CAUSE_OF_DESCRIPTION`.
   * @param {string} productName - The name of the product (e.g., 'caravan'), which may affect field mappings.
   * @param {Object} questionFields - Fields object that determines if claims or convictions mappings should be created.
   * @returns {Array<Object>} A formatted array of field previews, each containing `prepend`, `value`, and `append` for display.
   */
  const generateClaimsConvictionsPreviewList = (dataArray, item, constants, productName, questionFields) => {
    const data = dataArray.find(entry => entry.id === item.id);
    if (!data) return [];

    const mappings = questionFields.claims !== undefined
      ? createMappingsClaims(item, constants, productName, questionFields.field)
      : createMappingsConvictions(item, constants);

    // Build the preview list based on valid mappings entries in `data`
    return Object.entries(mappings).reduce((acc, [field, config]) => {
      const fieldExists = Object.prototype.hasOwnProperty.call(data, field);
      const isFieldValid = ![null, undefined, ''].includes(data[field]);
      const condition = !config.condition || config.condition();

      // Add field to the accumulator if it exists, is valid, and meets any specified condition
      if (fieldExists && isFieldValid && condition) {
        acc.push({
          prepend: config.prepend,
          value: config.value,
          append: config.append,
          error: config.error,
        });
      }

      return acc;
    }, []);
  };

  return {
    // methods
    vehicleIcon,
    generateClaimsConvictionsPreviewList,
  };
};
